// FLAGS: none
// Paginated list of the Workplace Sessions a client has joined.
import { apiPaths, apiPathsWithParams, pageSize } from "@src/constants";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fiveMinutes } from "@src/queries/staletimes";
import { Spaces } from "@src/api/v1/Spaces";
import {
  GetHostedSessionsParams,
  ResponseConveneHostableSessionResponse,
  ResponseConveneHostedSessionResponsePaginated,
  ResponseSpacesGetClientSpaceResponsePaginated,
} from "@src/api/v1/data-contracts";
import { OpenUpHttpClient } from "@src/api/OpenUpHttpClient";

export const useGetClientSpacesQuery = (
  clientId: string | undefined,
  page: number = 0,
  limit: number = pageSize,
) =>
  useQuery<ResponseSpacesGetClientSpaceResponsePaginated>({
    queryKey: [apiPathsWithParams.getClientSpaces(clientId!), page, limit],
    queryFn: () =>
      new Spaces(OpenUpHttpClient).getClientSpaces({
        clientId: clientId!,
        page,
        limit,
      }),
    enabled: !!clientId,
    staleTime: fiveMinutes,
  });

// `enabled` lets a caller list only 1:1 sessions without firing this request, for a status facet
// that hosted spaces cannot express.
export const useHostedSpacesQuery = (
  params: GetHostedSessionsParams,
  enabled: boolean = true,
) =>
  useQuery<ResponseConveneHostedSessionResponsePaginated>({
    queryKey: [apiPaths.hostedSpaces, params],
    queryFn: () => new Spaces(OpenUpHttpClient).getHostedSessions(params),
    enabled,
    staleTime: fiveMinutes,
  });

// The returned link embeds a short-lived Zoom token — request it per click, never cache or store.
export const useSpaceStartLinkMutation = () =>
  useMutation({
    mutationFn: (spaceId: string) =>
      new Spaces(OpenUpHttpClient).getStartLink(spaceId),
  });

// The scheduled group sessions the expert may offer to host (OI-25627). The window is decided
// server-side, so a stale list only ever shows a session the next click will refuse with a
// structured 400; a short staleTime keeps that rare.
export const useOpenForHostingQuery = () =>
  useQuery<ResponseConveneHostableSessionResponse[]>({
    queryKey: [apiPaths.openForHosting],
    queryFn: () => new Spaces(OpenUpHttpClient).getOpenForHosting(),
    staleTime: fiveMinutes,
  });

// Both mutations touch the paired part of a two-part topic as well, so the whole list is
// refetched rather than one row patched.
export const useHostSignupMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (spaceId: string) =>
      new Spaces(OpenUpHttpClient).signUpToHost(spaceId),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: [apiPaths.openForHosting] }),
  });
};

export const useWithdrawHostSignupMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (spaceId: string) =>
      new Spaces(OpenUpHttpClient).withdrawHostSignup(spaceId),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: [apiPaths.openForHosting] }),
  });
};

// Stepping down as host also gives up the paired part and reopens both for sign-up, so what the expert hosts
// and what is open are both refetched.
export const useWithdrawAsHostMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (spaceId: string) =>
      new Spaces(OpenUpHttpClient).withdrawAsHost(spaceId),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: [apiPaths.openForHosting] }),
        queryClient.invalidateQueries({ queryKey: [apiPaths.hostedSpaces] }),
      ]),
  });
};
